import { useState } from 'react';
import { Form, useActionData } from "@remix-run/react";
import { trackEvent, EventCategory, EventAction } from '~/utils/analytics';
import { getFormProps, getInputProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Field } from "~/components/forms";
import { z } from "zod";
import { useIsPending } from '~/utils/misc';
import { Separator } from "~/components/ui/separator";
import { SocialLoginButtons } from "~/components/socialLoginButtons";

const schema = z.object({
  email: z.string().email("Por favor, introduce un email válido").optional(),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres").optional(),
  loginType: z.enum(['email', 'password', 'reset', 'google', 'facebook']),
  redirectTo: z.string().optional(),
});

export function LoginForm({ redirectTo }: { redirectTo: string }) {
  const [loginType, setLoginType] = useState<'password' | 'email' | 'reset' | 'google' | 'facebook'>('password');
  const actionData = useActionData<{ status: string, email: string, error?: string }>();
  
  const [form, fields] = useForm({
    id: 'login-form',
    defaultValue: { redirectTo, loginType: 'password' },
    constraint: getZodConstraint(schema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: 'onSubmit',
  });

  const isPending = useIsPending();
  const sentSuccess = actionData?.status === 'success';
  const showPasswordField = loginType === 'password';

  // Handler for Google login
  const handleGoogleLogin = () => {
    trackEvent({
      category: EventCategory.USER,
      action: EventAction.CLICK,
      label: 'google-login',
    });
    
    const form = document.getElementById('login-form') as HTMLFormElement;
    const loginTypeInput = form.querySelector('input[name="loginType"]') as HTMLInputElement;
    loginTypeInput.value = 'google';
    form.submit();
  };

  // Handler for Facebook login
  const handleFacebookLogin = () => {
    trackEvent({
      category: EventCategory.USER,
      action: EventAction.CLICK,
      label: 'facebook-login',
    });
    
    const form = document.getElementById('login-form') as HTMLFormElement;
    const loginTypeInput = form.querySelector('input[name="loginType"]') as HTMLInputElement;
    loginTypeInput.value = 'facebook';
    form.submit();
  };

  // // Handler for passwordless login
  // const handlePasswordlessLogin = () => {
  //   setLoginType('email');
  //   trackEvent({
  //     category: EventCategory.USER,
  //     action: EventAction.CLICK,
  //     label: 'passwordless-login',
  //   });
  // };

  // Handler for forgot password
  const handleForgotPassword = () => {
    setLoginType('reset');
    trackEvent({
      category: EventCategory.USER,
      action: EventAction.CLICK,
      label: 'forgot-password',
    });
  };

  const getSubmitButtonText = () => {
    if (isPending) return 'Procesando...';
    
    switch (loginType) {
      case 'password':
        return 'Iniciar sesión';
      case 'reset':
        return 'Restablecer contraseña';
      case 'email':
        return 'Enviar enlace de acceso';
      default:
        return 'Iniciar sesión';
    }
  };

  const getFormDescription = () => {
    switch (loginType) {
      case 'reset':
        return 'Te enviaremos instrucciones para restablecer tu contraseña';
      case 'email':
        return 'Accede con tu email, si no tienes cuenta la crearemos';
      default:
        return 'Elige tu método de acceso preferido';
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h2 className="text-2xl font-bold">Accede a tu cuenta</h2>
        <p className="text-muted-foreground">{getFormDescription()}</p>
      </div>
      <div className="space-y-4">
        {/* Social Login Buttons - Only show if not in reset or email mode */}
        {loginType !== 'reset' && loginType !== 'email' && (
          <div className="space-y-3">
            {/* Social Login using existing component */}
            <SocialLoginButtons
              onGoogleLogin={handleGoogleLogin}
              onFacebookLogin={handleFacebookLogin}
              isPending={isPending}
            />

            {/* Passwordless Login Button - temporarily disabled */}
            {/*
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handlePasswordlessLogin}
              disabled={isPending}
            >
              Acceso sin contraseña
            </Button>
            */}
          </div>
        )}
        {/* Separator - Only show if not in email or reset mode */}
        {loginType !== 'email' && loginType !== 'reset' && (
          <Separator className="w-full bg-neutral-400 mb-16" style={{ margin: "24px 0" }}/>
        )}

        {/* Email/Password Form */}
        <Form
          method="POST"
          action="/login"
          {...getFormProps(form)}
          onSubmit={() => {
            trackEvent({
              category: EventCategory.FORM,
              action: EventAction.SUBMIT,
              label: `login-${loginType}`,
            });
          }}
        >
         {loginType === 'password' && (
           <CardDescription className="mb-4"> o Accede con tu email y contraseña</CardDescription>
         )}

          <div className="space-y-4">
            <AuthenticityTokenInput />
            <input type="hidden" name="loginType" value={loginType} />
            <input type="hidden" name="redirectTo" value={redirectTo} />
            
            {/* Email Field */}
            <Field 
              labelProps={{ 
                htmlFor: fields.email.id, 
                children: 'Email' 
              }}
              inputProps={{
                ...getInputProps(fields.email, { type: 'email' }),
                placeholder: 'tu@email.com',
                autoComplete: 'email'
              }}
              errors={fields.email.errors}
            />

            {/* Password Field - Only show if in password mode */}
            {showPasswordField && (
              <Field
                labelProps={{
                  htmlFor: fields.password.id,
                  children: 'Contraseña'
                }}
                inputProps={{
                  ...getInputProps(fields.password, { type: 'password' }),
                  placeholder: '••••••••',
                  autoComplete: 'current-password'
                }}
                errors={fields.password.errors}
              />
            )}

            {/* Error Message */}
            {actionData?.error && (
              <p className="text-sm text-destructive text-center">
                {actionData.error}
              </p>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isPending}
            >
              {getSubmitButtonText()}
            </Button>

            {/* Forgot Password Link - Only show if in password mode */}
            {showPasswordField && (
              <Button
                type="button"
                variant="link"
                className="text-sm w-full"
                onClick={handleForgotPassword}
              >
                ¿Olvidaste tu contraseña?
              </Button>
            )}

            {/* Back to login link - Show if in reset or email mode */}
            {(loginType === 'reset' || loginType === 'email') && (
              <Button
                type="button"
                variant="link"
                className="text-sm w-full"
                onClick={() => setLoginType('password')}
              >
                Volver al inicio de sesión
              </Button>
            )}
          </div>
        </Form>

        {/* Success Message */}
        {sentSuccess && (
          <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
            <p className="text-sm text-center">
              Te hemos enviado un {loginType === 'reset' ? 'enlace para restablecer tu contraseña' : 'enlace de acceso'} a: <strong>{actionData?.email}</strong>
            </p>
            <p className="text-sm text-center mt-2">
              Por favor, revisa tu bandeja de entrada y sigue las instrucciones.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
